import { recordLoginSession } from '../../api/audit';
import { useEffect, useRef, useState } from "react";
import '../../style/login.css';
import { setCookie } from '../../utils/cookie';
import BackgroundImage from '../../assets/gobg.jpg';
import { Input, Button, ConfigProvider, message, Space, Spin, Modal, Alert } from 'antd';
import { sendEmailVerification } from 'firebase/auth';
import { clearAdminCredentials, isEmailNotVerified, signOutAdmin, verifiedAdminToken } from '../../firebase/admin_session';
import { useLoginWithMFA } from "./use_mfa_callback";
import { LoadingOutlined } from "@ant-design/icons";

const { Password, OTP } = Input;

const LoginPanel = (props) => {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState();
    const [pwd, setPwd] = useState();
    const [needRedirect, setNeedRedirect] = useState(false);
    const [loading, setLoading] = useState(false);
    const [unverifiedUser, setUnverifiedUser] = useState(null);
    const [verificationBusy, setVerificationBusy] = useState(false);
    const [verificationError, setVerificationError] = useState('');
    const verificationBusyRef = useRef(false);
    const handledUserRef = useRef(null);
    const pendingUserRef = useRef(null);
    const total = 60;
    const timerRef = useRef(null);
    const [counter, setCounter] = useState(0);
    const [canResend, setCanResend] = useState(false);

    const {
        user,
        loading: authLoading,
        error: mfaError,
        mfaRequired,
        phoneNumber,
        loginWithEmail,
        verifyCode,
        resendCode,
    } = useLoginWithMFA();

    useEffect(() => {
        return () => {
            const pending = pendingUserRef.current;
            pendingUserRef.current = null;
            if (pending) void signOutAdmin(pending.auth).catch(() => {});
        };
    }, [])

    useEffect(() => {
        if (username) {
            setNeedRedirect(true);
        }
    }, [username]);

    useEffect(() => {
        if (needRedirect) {
            window.location.reload();
        }
    }, [needRedirect])

    const emailInput = (evt) => {
        setEmail(evt.target.value)
    }

    const pwdInput = (evt) => {
        setPwd(evt.target.value);
    }

    const confirmLogin = async () => {
        if (!email || !pwd || loading || authLoading || unverifiedUser) return;
        handledUserRef.current = null;
        try {
            await loginWithEmail(email, pwd);
        } catch (error) {
            // MFA 错误会在 useEffect 中处理
            if (!error.message?.includes('multi-factor-auth-required')) {
                setLoading(false);
                message.error(error.message || 'Sign in failed, please try again');
            }
        }
    }

    // 处理 MFA 登录成功
    useEffect(() => {
        if (user && handledUserRef.current !== user) {
            handledUserRef.current = user;
            handleLoginSuccess(user);
        }
    }, [user]);

    // 处理 MFA 错误
    useEffect(() => {
        if (mfaError) {
            setLoading(false);
            message.error(mfaError);
        }
    }, [mfaError]);


    const handleLoginSuccess = async (firebaseUser) => {
        setLoading(true);
        try {
            let token;
            try {
                token = await verifiedAdminToken(firebaseUser);
            } catch (error) {
                if (!isEmailNotVerified(error)) throw error;
                clearAdminCredentials();
                pendingUserRef.current = firebaseUser;
                setUnverifiedUser(firebaseUser);
                setVerificationError('');
                setLoading(false);
                return;
            }
            setCookie('user-token', token);
            await recordLoginSession();
            setUsername(firebaseUser.email);
        } catch (error) {
            await signOutAdmin(firebaseUser.auth).catch(() => {});
            setLoading(false);
            message.error(error.message || 'Login processing failed');
        }
    };

    const finishVerification = async () => {
        const pending = pendingUserRef.current;
        pendingUserRef.current = null;
        try {
            if (pending) await signOutAdmin(pending.auth);
        } catch {
            message.error('退出认证会话失败，请刷新页面后重试。');
        } finally {
            setUnverifiedUser(null);
            setVerificationError('');
        }
    };

    const cancelVerification = async () => {
        if (verificationBusyRef.current) return;
        verificationBusyRef.current = true;
        setVerificationBusy(true);
        try { await finishVerification(); }
        finally { verificationBusyRef.current = false; setVerificationBusy(false); }
    };

    const sendVerification = async () => {
        if (verificationBusyRef.current || !pendingUserRef.current) return;
        verificationBusyRef.current = true;
        setVerificationBusy(true);
        setVerificationError('');
        try {
            // Sending is exclusively a consequence of this explicit button click.
            await sendEmailVerification(pendingUserRef.current);
            message.success('验证邮件已发送，请完成验证后重新登录。');
            await finishVerification();
        } catch (error) {
            setVerificationError(error.code === 'auth/too-many-requests'
                ? '发送过于频繁，请稍后再试。'
                : '验证邮件发送失败，请检查网络后重试。');
        } finally {
            verificationBusyRef.current = false;
            setVerificationBusy(false);
        }
    };


    const onChange = async (text) => {
        await verifyCode(text);
    };

    const sharedProps = {
        onChange,
    };

    useEffect(() => {
        if (mfaRequired) {
            setCounter(0);
            setCanResend(false);
            startSeconds();
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [mfaRequired])

    useEffect(() => {
        if (counter >= total) {
            setCanResend(true);
            clearInterval(timerRef.current)
        }
    }, [counter])

    const startSeconds = () => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setCounter((value) => (value + 1))
        }, 1000)
    }

    const sendOtp = async () => {
        if (authLoading) return;
        const sent = await resendCode();
        if (sent) {
            setCounter(0);
            setCanResend(false);
            startSeconds();
        }
    }

    const getSecondsDesc = () => {
        return `${total - counter}s`
    }

    return (
        <ConfigProvider theme={{
            token: { colorPrimary: '#0266FF' },
            components: {
                Button: {
                    colorBgContainerDisabled: '#D0DFFC',
                    colorBgContainer: '#FFFFFF',
                    colorTextDisabled: 'white',
                    borderColorDisabled: '#D0DFFC'
                }
            }
        }}>
            <Modal
                open={!!unverifiedUser}
                title="邮箱未验证"
                okText="发送验证邮件"
                cancelText="取消"
                onOk={sendVerification}
                onCancel={cancelVerification}
                confirmLoading={verificationBusy}
                cancelButtonProps={{
                    disabled: verificationBusy,
                    style: {
                        backgroundColor: '#FFFFFF',
                        color: verificationBusy ? '#868FA0' : '#21232E',
                        borderColor: '#D7D9DD',
                    },
                }}
                closable={!verificationBusy}
                keyboard={!verificationBusy}
                maskClosable={!verificationBusy}
            >
                <p>你的邮箱未验证，是否发送验证邮件？</p>
                <p>{unverifiedUser?.email}</p>
                {verificationError && <Alert type="error" showIcon message={verificationError} />}
            </Modal>
            <div className="main" style={{ backgroundImage: `url(${BackgroundImage})` }}>
                <div className="login-form">
                    <h1>Addx Go Admin</h1>
                    <div className="head">
                        <img src='/images/logo.png' alt="" />
                    </div>

                    {
                        mfaRequired ?
                            <div style={{ textAlign: 'center' }}>
                                <div className="f-dark f-s-26 text-center">Multi-Factor Authentication</div>
                                <div className="f-gray f-r-14 mt-1 text-center">
                                    Please enter the verification code sent to your phone number:
                                    <label className="f-dark">{phoneNumber}</label>
                                </div>

                                <div className="mt-12 f-s-26" style={{width: 360, margin: '16px auto'}}>
                                    <OTP disabled={authLoading} type="number" size="large" style={{ width: '100%' }} variant="filled" {...sharedProps} />
                                </div>

                                <Space className="mt-6">
                                    {
                                        canResend && !authLoading ?
                                            <div className="f-r-14 f-blue resend select-none cursor-pointer" onClick={sendOtp}>Resend Code</div>
                                            :
                                            <div className="f-r-14 f-gray">
                                                Resend Code ({getSecondsDesc()})
                                            </div>
                                    }
                                    {authLoading && <Spin indicator={<LoadingOutlined />} />}
                                </Space>
                            </div>
                            :
                            <div style={{ margin: '0 32px' }}>
                                <div className="email mt-10">
                                    <div className="f-dark f-r-14">Email address</div>
                                    <div className="mt-1 relative">
                                        <Input className="input f-r-16" placeholder="Enter your email address" onChange={emailInput} allowClear />
                                    </div>
                                </div>

                                <div className="email mt-4">
                                    <div className="f-dark f-r-14">Password</div>
                                    <div className="mt-1">
                                        <Password className="input f-r-16" placeholder="Input password" onChange={pwdInput} allowClear />
                                    </div>
                                </div>

                                <Button loading={loading || authLoading} onClick={confirmLogin} disabled={!email || !pwd || !!unverifiedUser} type="primary" className="mt-10 f-m-16 next-btn mb-1" block>Log in</Button>

                            </div>
                    }

                    <div id="recaptcha-container"></div>
                    <div style={{ height: 32 }} />
                </div>
            </div>
        </ConfigProvider>
    );
}

export default LoginPanel;
